/**
 * Filtro de periodo da aba Processos — as regras do prototipo, sem o mock de data.
 *
 * - `hoje` e `7d` contam dias CORRIDOS para tras, cruzando o mes quando preciso.
 * - `mes` e `mes_anterior` (25/09) sao a COMPETENCIA fechada: do dia 1 ao ultimo
 *   dia do mes. Substituiram o `30d`, que misturava duas competencias — no dia
 *   25, "30 dias" ia de 26/08 a 25/09 e somava trabalho de agosto com o de
 *   setembro num numero so'.
 *
 *   Isto REVERTE em parte a decisao de 21/09 (que removeu o clamp do dia 1
 *   porque "a tela serve para CONFERIR REGISTROS, nao para fechar competencia").
 *   O pedido de 25/09 e' explicito no sentido oposto: "no filtro de 30 dias,
 *   traga apenas mes fechado". O `7d` fica justamente para quem precisa
 *   atravessar a virada — no dia 1, `mes` traz so' o dia 1, e e' o preset errado
 *   para conferir o que rodou ontem.
 * - Editar os inputs livres zera o preset (nenhum botao destacado).
 * - A data corrente e' REAL (o `HOJE_MOCK` do prototipo nao migra); `hoje` e'
 *   parametro so' para os testes serem deterministicos.
 */

import type { IPeriodoParams } from '../api';

export type PresetPeriodo = 'hoje' | '7d' | 'mes' | 'mes_anterior';

/** Datas date-only (YYYY-MM-DD); `preset` null = intervalo editado a mao. */
export interface PeriodoProcessos {
  preset: PresetPeriodo | null;
  de: string;
  ate: string;
}

/** Formata local, sem toISOString — UTC-3 estouraria o dia perto da meia-noite. */
export const isoDia = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(
    2,
    '0',
  )}`;

export const rangeDoPreset = (preset: PresetPeriodo, hoje = new Date()): PeriodoProcessos => {
  const dia = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());

  // MES ATUAL: do dia 1 ate' HOJE, nao ate' o fim do mes. O futuro nao tem
  // execucao, e esticar o `ate` faria o intervalo prometer dias que nem
  // chegaram — o backend filtra por `ts <= ate`, entao o numero seria o mesmo,
  // mas o rotulo do periodo mentiria sobre o que esta' sendo olhado.
  if (preset === 'mes') {
    return {
      preset,
      de: isoDia(new Date(dia.getFullYear(), dia.getMonth(), 1)),
      ate: isoDia(dia),
    };
  }

  // MES ANTERIOR: competencia FECHADA, do dia 1 ao ultimo dia. `new Date(ano,
  // mes, 0)` devolve o ultimo dia do mes anterior — resolve 28/29/30/31 sem
  // tabela, inclusive fevereiro bissexto, e vira o ano sozinho em janeiro.
  if (preset === 'mes_anterior') {
    return {
      preset,
      de: isoDia(new Date(dia.getFullYear(), dia.getMonth() - 1, 1)),
      ate: isoDia(new Date(dia.getFullYear(), dia.getMonth(), 0)),
    };
  }

  const inicio = new Date(dia);
  if (preset === '7d') inicio.setDate(inicio.getDate() - 6);
  return {
    preset,
    de: isoDia(inicio),
    ate: isoDia(dia),
  };
};

/**
 * Converte para os query-params dos endpoints (`date_init`/`date_end` ISO), cobrindo o
 * dia inteiro nas pontas. Intervalo incompleto (um input limpo) vai SEM filtro — o
 * backend entao usa a janela padrao, em vez de receber uma ponta solta.
 */
export const paraParams = (p: PeriodoProcessos): IPeriodoParams => {
  if (!p.de || !p.ate) return {};
  return {
    date_init: new Date(`${p.de}T00:00:00`).toISOString(),
    date_end: new Date(`${p.ate}T23:59:59.999`).toISOString(),
  };
};
